import { useEffect, useState } from 'react'
import { isDemoSession } from '../../../../../data/demoSession'
import type { ServerApiClient } from '../../../../../data/ServerApiClient'
import { buildCfoRoutePath, parseCfoRouteState } from '../../cfoRoutes'
import { CFO_EVENTS, CFO_SURFACES } from '../../cfoTelemetry'
import { useAnalytics } from '../../../../../analytics'
import type { DemoCompany } from '../ceoCockpitTypes'
import demoCompaniesJson from '../fixtures/demo-companies.json'

const CACHE_KEY = 'fg_ceo_demo_companies'

export function CeoDemoCompanyStrip({
  currentSlug,
  appApi,
}: {
  currentSlug?: string | null
  appApi?: ServerApiClient | null
}) {
  const analytics = useAnalytics()
  const [companies, setCompanies] = useState<DemoCompany[]>(() => readCached() || demoCompaniesJson.companies as DemoCompany[])
  const demo = typeof window !== 'undefined' && isDemoSession()

  useEffect(() => {
    if (!demo) return
    const cached = readCached()
    if (cached?.length) setCompanies(cached)
    if (!appApi?.getDemoCompanies) return
    appApi.getDemoCompanies()
      .then((out) => {
        const next = out?.companies || []
        if (next.length) {
          setCompanies(next)
          try {
            window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(next))
          } catch {
            /* ignore */
          }
        }
      })
      .catch(() => {
        /* keep cached roster */
      })
  }, [appApi, demo])

  if (!demo || !companies.length) return null

  return (
    <div className="ceo-demo-strip" data-testid="ceo-demo-strip">
      <span>See a different business:</span>
      <div className="ceo-demo-chips">
        {companies.map((company) => {
          const active = company.slug === currentSlug
          return (
            <button
              type="button"
              key={company.slug}
              className={active ? 'is-active' : undefined}
              disabled={active}
              onClick={() => {
                analytics.track({
                  event: CFO_EVENTS.ceoDemoCompanySwitch,
                  surface: CFO_SURFACES.ceo,
                  props: { slug: company.slug, industry_key: company.industry_key },
                })
                const route = typeof window !== 'undefined' ? parseCfoRouteState(window.location) : null
                const href = buildCfoRoutePath({
                  siteSlug: company.site_slug || route?.siteSlug || 'rivertown-advisory',
                  workbookId: company.workbook_id,
                  page: 'ceo',
                })
                if (href && href !== '/') window.location.assign(href)
              }}
            >
              {company.name}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function readCached(): DemoCompany[] | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}
